import * as THREE from 'three';
import { ft } from '../core/units.js';
import { LINENS, OBJECTS } from '../data/objects.js';
import { chairStack, linenStack, merged, roundFolded } from './models.js';
import { propGeometry, propMaterials } from './props.js';

/**
 * Draws every cart in the building, and what is stacked on each.
 *
 * The load is drawn, not summarised. A cart whose contents only exist in the
 * HUD is a cart you have to read a number off; a cart with four rounds stacked
 * on it is one you can glance at from across the storeroom and know. That is
 * worth the handful of pooled meshes it costs.
 *
 * Pooled per cart rather than per kind, because there are seven of them and
 * they are all the same few parts at different scales: a deck on casters, a
 * handle. What is on the deck is drawn as what it is where there is a model
 * for it (`models.js`): chairs nested in a stack, linen folded, rounds folded
 * in half and stood on edge along the deck. Anything else is a slab per few.
 */
export function createCartView({ wheeled }) {
  const group = new THREE.Group();
  group.name = 'carts';

  const disposables = [];
  const keep = (thing) => {
    disposables.push(thing);
    return thing;
  };

  const deckMaterial = keep(new THREE.MeshStandardMaterial({ color: 0x5d636d, roughness: 0.85 }));
  const frameMaterial = keep(new THREE.MeshStandardMaterial({ color: 0x40454e, roughness: 0.9 }));
  const boxGeometry = keep(new THREE.BoxGeometry(1, 1, 1));
  const wheelGeometry = keep(new THREE.CylinderGeometry(ft(0.2), ft(0.2), ft(0.12), 14).rotateZ(Math.PI / 2));
  const rubberMaterial = keep(new THREE.MeshStandardMaterial({ color: 0x151515, roughness: 0.9 }));
  const chairFrameMaterial = keep(new THREE.MeshStandardMaterial({ color: 0xb8963e, metalness: 0.7, roughness: 0.35 }));
  const roundLegMaterial = keep(new THREE.MeshStandardMaterial({ color: 0x3a3a3c, metalness: 0.5, roughness: 0.5 }));

  /** Load geometry, built once per shape of load and shared by every cart. */
  const loads = new Map();
  function loadGeometry(key, build, parts) {
    if (!loads.has(key)) loads.set(key, keep(merged(build(), parts)));
    return loads.get(key);
  }
  /** A prop's own model, once per kind, for a load of them. */
  const propGeometries = new Map();
  function propOf(kind) {
    if (!propGeometries.has(kind)) {
      const row = OBJECTS[kind];
      const geometry = row ? propGeometry(kind, ft((row.radius ?? 0.4) * 2), ft(row.height ?? 0.4), row.colour) : null;
      if (geometry) keep(geometry);
      propGeometries.set(kind, geometry);
    }
    return propGeometries.get(kind);
  }

  /** Chairs stacked, linens in two piles, rounds on edge: or null, for a slab. */
  function modelledLoad(kind, count) {
    if (kind === 'chair') {
      const n = Math.min(count, 12);
      return { geometry: loadGeometry(`chair:${n}`, () => chairStack(n), ['frame', 'cushion']), parts: ['frame', 'body'] };
    }
    if (kind === 'linen') {
      const n = Math.min(Math.ceil(count / 2), 10);
      const two = Math.min(count, 2);
      return {
        geometry: loadGeometry(`linen:${n}:${two}`, () => {
          const out = { linen: [] };
          for (let i = 0; i < two; i += 1) out.linen.push(...linenStack(i === 0 ? n : Math.max(1, count - n), 0.34, 0.26).linen.map((g) => g.translate(0, 0, (i - (two - 1) / 2) * 0.3)));
          return out;
        }, ['linen']),
        parts: ['body'],
      };
    }
    if (kind === 'round') {
      const n = Math.min(count, 6);
      return {
        geometry: loadGeometry(`round:${n}`, () => {
          const out = { top: [], legs: [] };
          for (let i = 0; i < n; i += 1) {
            const one = roundFolded(0.07);
            for (const [part, list] of Object.entries(one)) out[part].push(...list.map((g) => g.rotateY(Math.PI / 2).translate((i - (n - 1) / 2) * 0.085, 0, 0)));
          }
          return out;
        }, ['top', 'legs']),
        parts: ['body', 'legs'],
      };
    }
    return null;
  }

  const loadMaterials = new Map();
  function loadMaterial(kind, variant) {
    const definition = OBJECTS[kind];
    const colour =
      variant && definition?.variantSet === 'linenColour'
        ? LINENS[variant]?.colour ?? definition.colour
        : definition?.colour ?? 0x9a7a52;
    let found = loadMaterials.get(colour);
    if (!found) {
      found = new THREE.MeshStandardMaterial({ color: colour, roughness: 0.9 });
      loadMaterials.set(colour, found);
      disposables.push(found);
    }
    return found;
  }

  /** One node per cart: a deck, four legs, a handle, and up to six load slabs. */
  const rigs = wheeled.all.map((cart) => {
    const node = new THREE.Group();
    const width = ft(cart.definition.radius) * 1.05;
    const long = ft(cart.definition.radius) * 1.35;

    const body = new THREE.Mesh(boxGeometry, deckMaterial);
    body.scale.set(width, ft(0.3), long);
    body.position.y = ft(1.1);
    body.castShadow = true;
    body.receiveShadow = true;
    node.add(body);

    // A caster at each corner: a fork down from the deck, and a wheel.
    for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      const fork = new THREE.Mesh(boxGeometry, frameMaterial);
      fork.scale.set(ft(0.22), ft(0.75), ft(0.22));
      fork.position.set((sx * width) / 2.6, ft(0.6) + ft(0.37), (sz * long) / 2.6);
      fork.castShadow = true;
      node.add(fork);
      const wheel = new THREE.Mesh(wheelGeometry, rubberMaterial);
      wheel.position.set((sx * width) / 2.6, ft(0.2), (sz * long) / 2.6);
      wheel.castShadow = true;
      node.add(wheel);
    }
    // A bumper round the deck's edge.
    for (const [sx, sz, lx, lz] of [[0, -1, 1, 0], [0, 1, 1, 0], [-1, 0, 0, 1], [1, 0, 0, 1]]) {
      const bumper = new THREE.Mesh(boxGeometry, rubberMaterial);
      bumper.scale.set(lx ? width + ft(0.1) : ft(0.1), ft(0.18), lz ? long + ft(0.1) : ft(0.1));
      bumper.position.set((sx * width) / 2, ft(1.1), (sz * long) / 2);
      node.add(bumper);
    }

    const handle = new THREE.Mesh(boxGeometry, frameMaterial);
    handle.scale.set(width * 0.9, ft(0.2), ft(0.2));
    handle.position.set(0, ft(2.6), long * 0.52);
    node.add(handle);

    for (const sx of [-1, 1]) {
      const post = new THREE.Mesh(boxGeometry, frameMaterial);
      post.scale.set(ft(0.2), ft(1.6), ft(0.2));
      post.position.set((sx * width) / 2.4, ft(1.9), long * 0.52);
      node.add(post);
    }

    /*
     * Seats and a roof, for a kind whose row has them. Drawn from the same
     * numbers the rules read, so what you see covered is what is covered.
     */
    for (const seat of cart.definition.seats ?? []) {
      const bench = new THREE.Mesh(boxGeometry, deckMaterial);
      bench.scale.set(ft(1.6), ft(0.4), ft(1.4));
      bench.position.set(ft(seat.at[0]), ft(seat.from) - ft(0.2), ft(seat.at[1]));
      node.add(bench);
    }
    const canopy = cart.definition.canopy;
    if (canopy) {
      const [x0, z0, x1, z1] = canopy.rect;
      const roof = new THREE.Mesh(boxGeometry, frameMaterial);
      roof.scale.set(ft(Math.abs(x1 - x0)), ft(0.2), ft(Math.abs(z1 - z0)));
      roof.position.set(ft((x0 + x1) / 2), ft(canopy.at), ft((z0 + z1) / 2));
      roof.castShadow = true;
      node.add(roof);
      for (const [px, pz] of [[x0, z0], [x1, z0], [x0, z1], [x1, z1]]) {
        const post = new THREE.Mesh(boxGeometry, frameMaterial);
        post.scale.set(ft(0.15), ft(canopy.at) - ft(1.1), ft(0.15));
        post.position.set(ft(px), (ft(canopy.at) + ft(1.1)) / 2, ft(pz));
        node.add(post);
      }
    }

    const slabs = [];
    for (let i = 0; i < 6; i += 1) {
      const slab = new THREE.Mesh(boxGeometry, deckMaterial);
      slab.castShadow = true;
      slab.visible = false;
      node.add(slab);
      slabs.push(slab);
    }

    /*
     * A round truck, for the table cart with rounds on it.
     *
     * A 72 inch round folded in half is six feet long, and the table cart's
     * deck is two: there is no folding or stacking that fits one on it. So
     * the cart draws itself as what carries rounds, a long low truck with a
     * frame at each end and the rounds standing along it, and goes back to
     * being a table cart when they come off. Drawn only; the cart the game
     * pushes, tips and bumps is the same cart either way.
     */
    const plain = [...node.children];
    let truck = null;
    if (cart.definition.id === 'tablecart') {
      truck = new THREE.Group();
      const length = ft(6.4);
      const rail = (sx, sy, sz, x, y, z, material = frameMaterial) => {
        const mesh = new THREE.Mesh(boxGeometry, material);
        mesh.scale.set(sx, sy, sz);
        mesh.position.set(x, y, z);
        mesh.castShadow = true;
        truck.add(mesh);
      };
      rail(width * 0.9, ft(0.25), length, 0, ft(0.55), 0, deckMaterial);
      for (const sx of [-1, 1]) rail(ft(0.12), ft(0.2), length + ft(0.2), (sx * width) / 2, ft(0.75), 0);
      for (const sz of [-1, 1]) {
        // An end frame, the height of a folded round, that doubles as a handle.
        for (const sx of [-1, 1]) rail(ft(0.15), ft(3.1), ft(0.15), (sx * width) / 2.2, ft(2.15), (sz * length) / 2);
        rail(width, ft(0.15), ft(0.15), 0, ft(3.65), (sz * length) / 2);
        for (const sx of [-1, 1]) {
          const wheel = new THREE.Mesh(wheelGeometry, rubberMaterial);
          wheel.position.set((sx * width) / 2.6, ft(0.2), (sz * length) / 2.3);
          truck.add(wheel);
        }
      }
      truck.visible = false;
      node.add(truck);
    }

    // The modelled load, when there is one: a single mesh, re-pointed.
    const load = new THREE.Mesh(boxGeometry, [frameMaterial]);
    load.castShadow = true;
    load.visible = false;
    load.position.y = ft(1.25);
    node.add(load);

    group.add(node);
    return { cart, node, slabs, load, width, long, plain, truck, props: [] };
  });

  return {
    object3D: group,

    sync() {
      for (const rig of rigs) {
        const { cart, node } = rig;
        node.position.set(cart.position.x, cart.position.y, cart.position.z);
        node.rotation.y = cart.yaw;
        // A tipped cart is on its side. One rotation, no separate model.
        node.rotation.z = cart.tipped ? Math.PI / 2.4 : 0;

        const material = loadMaterial(cart.hold.kind, cart.hold.variant);
        const modelled = cart.hold.kind && cart.hold.count > 0 ? modelledLoad(cart.hold.kind, cart.hold.count) : null;
        rig.load.visible = Boolean(modelled);
        // Rounds ride on the truck; anything else, or nothing, on the cart.
        const asTruck = Boolean(rig.truck && modelled && cart.hold.kind === 'round');
        if (rig.truck) {
          rig.truck.visible = asTruck;
          for (const part of rig.plain) part.visible = !asTruck;
        }
        rig.load.position.y = asTruck ? ft(0.85) : ft(1.25);
        if (modelled) {
          rig.load.geometry = modelled.geometry;
          rig.load.material = modelled.parts.map((part) => (part === 'body' ? material : part === 'frame' ? chairFrameMaterial : roundLegMaterial));
        }
        // Anything else that has a model rides as itself: up to six of them on
        // the deck, two across and three along.
        const prop = cart.hold.kind && !modelled ? propOf(cart.hold.kind) : null;
        const copies = prop ? Math.min(cart.hold.count, 6) : 0;
        while (rig.props.length < copies) {
          const mesh = new THREE.Mesh(prop, propMaterials());
          mesh.castShadow = true;
          node.add(mesh);
          rig.props.push(mesh);
        }
        rig.props.forEach((mesh, i) => {
          mesh.visible = i < copies;
          if (!mesh.visible) return;
          mesh.geometry = prop;
          // One layer: two across, three along.
          mesh.position.set(((i % 2) - 0.5) * rig.width * 0.45, ft(1.25), (Math.floor(i / 2) - 1) * rig.long * 0.3);
        });
        const perSlab = cart.hold.kind === 'round' ? 1 : 4;
        const shown = cart.hold.kind && !modelled && !prop ? Math.min(Math.ceil(cart.hold.count / perSlab), 6) : 0;
        for (let i = 0; i < rig.slabs.length; i += 1) {
          const slab = rig.slabs[i];
          slab.visible = i < shown;
          if (!slab.visible) continue;
          slab.material = material;
          slab.scale.set(rig.width * 0.92, ft(0.34), rig.long * 0.9);
          slab.position.set(0, ft(1.35) + i * ft(0.36), 0);
        }
      }
    },

    dispose() {
      for (const thing of disposables) thing.dispose?.();
      group.removeFromParent();
    },
  };
}
